import React, { useState, useEffect, useRef } from "react";
import { Header } from "../components/Header";
import { EventGrid } from "../components/EventGrid";
import { MemoriesSection } from "../components/MemoriesSection";
import { CreateEventsPage } from "../components/CreateEventsPage";
import { HelpPage } from "../components/HelpPage";
import { PrivacyPolicyPage } from "../components/PrivacyPolicyPage";
import { CookiePolicyPage } from "../components/CookiePolicyPage";
import { MobileSearch } from "../components/MobileSearch";
import { MobileFooter } from "../components/MobileFooter";
import { EventRouteRedirect } from "../components/EventRouteRedirect";
import { AppDownloadPrompt } from "../components/AppDownloadPrompt";

import { Footer } from "../components/Footer";
import { Toaster } from "../components/Toaster";
import { DemoIndicator } from "../components/DemoIndicator";

export default function App() {
  // Initialize current page based on URL path
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === "/produtor") return "produtor";
    if (path === "/ajuda") return "ajuda";
    if (path === "/politica-privacidade") return "politica-privacidade";
    if (path === "/politica-cookies") return "politica-cookies";
    if (/^\/evento\/[^/]+\/\d+\/?$/.test(path)) return "evento";
    return "home";
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchTrigger = useRef<HTMLButtonElement | null>(null);
  const openMobileSearch = (trigger: HTMLButtonElement) => {
    searchTrigger.current = trigger;
    setMobileSearchOpen(true);
  };

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.removeItem("theme");

    // Handle browser back/forward navigation
    const handlePopState = () => {
      setCurrentPage(getInitialPage());
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Set favicon
  useEffect(() => {
    const favicon =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ||
      document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/png";
    favicon.href = "https://uniqueingressos.com.br/assets/img/favicon.png";

    if (!document.querySelector('link[rel="icon"]')) {
      document.head.appendChild(favicon);
    }

    // Also set apple-touch-icon for better mobile support
    const appleTouchIcon =
      document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]') ||
      document.createElement("link");
    appleTouchIcon.rel = "apple-touch-icon";
    appleTouchIcon.href =
      "https://uniqueingressos.com.br/assets/img/favicon.png";

    if (!document.querySelector('link[rel="apple-touch-icon"]')) {
      document.head.appendChild(appleTouchIcon);
    }

    // Set document title based on current page
    const pageTitles = {
      home: "Unique Ingressos – A tecnologia por trás dos maiores eventos",
      produtor: "Produtor – Unique Ingressos",
      ajuda: "Central de Ajuda – Unique Ingressos",
      "politica-privacidade": "Política de Privacidade – Unique Ingressos",
      "politica-cookies": "Política de Cookies – Unique Ingressos",
      evento: "Evento – Unique Ingressos",
    };

    document.title = pageTitles[currentPage] || pageTitles["home"];
  }, [currentPage]);

  const handleNavigation = (page: string) => {
    // The home shortcut only returns to the top of the page already open.
    if (page === "home" && currentPage === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const ticketingOrigin = String(import.meta.env.VITE_REDIRECT_URL || "https://uniqueingressos.com.br").replace(/\/+$/, "");
    const url = page === "login" || page === "my-tickets"
      ? `${ticketingOrigin}/login`
      : page === "home" ? "/" : `/${page}`;
    window.open(url, "_blank", "noopener");
  };

  const isHomePage = currentPage === "home";

  const renderCurrentPage = () => {
    switch (currentPage) {
      case "produtor":
        return <CreateEventsPage onNavigate={handleNavigation} />;
      case "ajuda":
        return <HelpPage />;
      case "politica-privacidade":
        return <PrivacyPolicyPage onNavigate={handleNavigation} />;
      case "politica-cookies":
        return <CookiePolicyPage onNavigate={handleNavigation} />;
      case "evento":
        return <EventRouteRedirect />;
      case "home":
      default:
        return (
          <main className="relative">
            <div className="home-event-programming">
              <EventGrid />
            </div>
            <MemoriesSection />

          </main>
        );
    }
  };

  const shouldShowFooter = isHomePage ||
    ["produtor", "ajuda", "politica-privacidade", "politica-cookies"].includes(currentPage);

  return (
    <div className="unique-shell min-h-screen relative mobile-app-container">
      <Header currentPage={currentPage} onNavigate={handleNavigation} />
      <div className={isHomePage ? "mobile-homepage-wrapper" : "mobile-content-container"}>
        {renderCurrentPage()}
      </div>
      {shouldShowFooter && <>
        <Footer onNavigate={handleNavigation} />
        <div className="footer-mobile">
          <MobileFooter currentPage={currentPage} onNavigate={handleNavigation} onSearch={openMobileSearch} />
        </div>
      </>}
      <MobileSearch open={mobileSearchOpen} onOpenChange={setMobileSearchOpen} triggerRef={searchTrigger} />
      {isHomePage && <AppDownloadPrompt suppressed={mobileSearchOpen} />}
      <Toaster position="bottom-right" />
      <DemoIndicator />
    </div>
  );
}
